import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../HardwareFirstQuiz.module.scss';

/** Mirrors fi-quizz's EMAIL_RE so both quizzes accept the same addresses. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailStepProps {
  titleKey: string;
  value: string | null;
  onChange(email: string | null): void;
  /** Advance with a captured email. Only called when the address validates. */
  onSubmit(): void;
  /** Advance without one. The result still shows; no lead is captured. */
  onSkip(): void;
}

/**
 * The last question. Skipping is allowed by design: the visitor still reaches
 * the result, but with no email there is no consent signal, so the caller runs
 * no lead capture.
 */
export default function EmailStep({ titleKey, value, onChange, onSubmit, onSkip }: EmailStepProps) {
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);
  const email = value ?? '';
  const isValid = EMAIL_RE.test(email.trim());

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      setShowError(true);
      return;
    }
    onSubmit();
  };

  return (
    <form className={styles.stepBody} onSubmit={submit}>
      <h2 className={styles.stepTitle}>{t(titleKey)}</h2>
      <input
        // biome-ignore lint/a11y/noAutofocus: the sheet is a single-purpose dialog and the
        // keyboard should be up when this step opens
        autoFocus
        type="email"
        inputMode="email"
        autoComplete="email"
        enterKeyHint="next"
        className={styles.emailInput}
        placeholder={t('hf_quiz_email_placeholder')}
        aria-label={t(titleKey)}
        aria-invalid={showError}
        value={email}
        onChange={(e) => {
          setShowError(false);
          onChange(e.target.value || null);
        }}
      />
      {showError && <div className={styles.emailError}>{t('hf_quiz_invalid_email')}</div>}
      <button type="submit" className={styles.primaryCta}>
        {t('hf_quiz_next')}
      </button>
      <button type="button" className={styles.skip} onClick={onSkip}>
        {t('hf_quiz_skip')}
      </button>
    </form>
  );
}
